/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import {
    type BaseRequestOptionsArgs, type Command, type Filter, type HttpResponse, type Rest, BatchService, HttpService, LazyloadService,
    LoadingService, PromptService,
} from '@dm/core';
import { type Lun, type ProtectionLun } from 'app/common/types/mo';
import { isFunction } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, flatMap, map } from 'rxjs/operators';

import { ProtectLunStrService } from './lun-str.service';
import { UnavailableLunComponent } from './unavailable-lun/unavailable-lun.component';

export interface MixLun extends Lun, ProtectionLun { }

@Injectable({ providedIn: 'root' })
export class ProtectLunService {
    constructor(
        private http: HttpService,
        private str: ProtectLunStrService,
        private loadService: LoadingService,
        private promptService: PromptService,
        private batch: BatchService,
        private lazyloadService: LazyloadService,
    ) { }

    // 批量查询LUN
    getLUNRest(): Rest {
        return this.http.makeUrl('lun.getBatch')
            .setParams({ range: '[0-100]' })
            .setFilters([
                // 排除容器LUN 和 带内LUN
                { field: 'USAGETYPE', value: '5_6_7_8', matchMode: '!!' },
            ]);
    }

    /**
     * 查询lun
     * @param id
     */
    get(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('lun.get').format(id);
        return this.http.get(rest, options);
    }

    // 删除LUN Action
    deleteAction(luns, cb): void {
        const names = [];
        const cmds: Command[] = [];
        luns.forEach((lun) => {
            const cmd: Command = {
                name: `${this.str.lun.deleteLun} ${lun.NAME}`,
                action: this.delete(lun.ID, { doException: false }),
            };
            cmds.push(cmd);
            names.push(lun.NAME);
        });
        this.loadService.hide();
        this.promptService.warning({
            id: 'prompt-protect-lun-deleteAction',
            content: this.str.get('storage.lun_task_delete_msg',
                [names.join(', ')]),
            onOk: () => {
                this.promptService.close();
                this.batch.exePrompt(cmds, {
                    onClose: () => {
                        if (isFunction(cb)) {
                            cb(true);
                        }
                    },
                });
            },
            onCancel: () => {
                if (isFunction(cb)) {
                    cb(false);
                }
                this.promptService.close();
            },
            title: this.str.lun.deleteLun,
        });
    }

    // 删除LUN
    delete(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('lun.delete').format(id);
        return this.http.delete(rest, options);
    }

    getAllLUNProtect(): Rest {
        return this.http.makeUrl('lun.getAllLUNProtectCount', undefined, true);
    }

    getLUNProtectCount(): Rest {
        return this.http.makeUrl('lun.getLUNProtectCount');
    }

    /**
     * 修改LUN CDP定时快照开关
     */
    modifyLunProtectCdpEnableSwitch(params: { ID: string; cdpEnableSwitch: boolean }, options?: BaseRequestOptionsArgs)
        : Observable<HttpResponse> {
        const rest = this.http.makeUrl('lun.put');
        return this.http.put(rest, params, options);
    }

    toLun(data: ProtectionLun): MixLun {
        return {
            ID: data.ID,
            NAME: data.name,
            HEALTHSTATUS: data.healthStatus,
            RUNNINGSTATUS: data.runningStatus,
            CAPACITY: data.capacity,
            ...data,
        };
    }

    /**
     * 获取满足业务条件的LUN列表，查询失败时返回原来的LUN
     * @param title 动作标题
     * @param oriRest 查询满足业务LUN列表的rest
     * @param luns 需要检查的LUN，当前最多100个
     * @param specifyLun 是否使用指定的单个LUN
     */
    getAvailableLuns(title: string, oriRest: Rest, luns: Lun[], specifyLun?: boolean): Observable<Lun[]> {
        let rest = oriRest;
        if (!luns || !luns.length) {
            return of(luns);
        }

        rest = rest.clone();
        const groupFilters: Filter[][] = (rest as any).groupFilters || [];
        for (const lun of luns) {
            groupFilters.push([{ field: 'ID', matchMode: '::', value: lun.ID }]);
        }
        rest.setRange({ limit: luns.length, offset: 0 });
        rest.setGropFilters(groupFilters);
        return this.http.get(rest, { mask: true })
            .pipe(
                map(({ data }) => (data || []) as Lun[]),
                catchError(() => of(luns)),
                flatMap((filteredLuns: Lun[]) => this.unavailableLunPrompt(title, luns, filteredLuns, specifyLun)),
            );
    }

    /**
     * 不满足的LUN提示
     */
    private unavailableLunPrompt(title: string, luns: Lun[], filteredLuns: Lun[], specifyLun?: boolean): Observable<Lun[]> {
        if (filteredLuns.length === luns.length) {
            return of(filteredLuns);
        }
        // 指定的单个LUN不可用
        if (specifyLun && luns.length === 1 && !filteredLuns.length) {
            return new Observable<Lun[]>((observer) => {
                this.promptService.prompt({
                    title,
                    content: this.str.getUnavailableMsg(luns[0].NAME),
                    width: 600,
                    onOk: () => {
                        this.promptService.close();
                        observer.error('canceld');
                    },
                });
            });
        }
        return new Observable<Lun[]>((observer) => {
            const unavailableLuns: Lun[] = luns.filter((lun) => !filteredLuns.some((filteredLun) => lun.ID === filteredLun.ID));
            const component = this.lazyloadService.createComponent(UnavailableLunComponent, {
                luns: unavailableLuns,
            });

            this.promptService.promptConfirm({
                title,
                content: this.str.lun.filterLunMsg,
                width: 600,
                templateRef: component,
                onOk: () => {
                    this.promptService.close();
                    observer.next(filteredLuns);
                    observer.complete();
                },
                onCancel: () => {
                    this.promptService.close();
                    observer.error('canceld');
                },
            });
        });
    }
}
